/**
 * Service for managing user preferences and settings
 * Uses localStorage with Supabase sync for cross-platform persistence
 */

import { UserPreferencesService, AppSettings } from './userPreferencesService';

// Simple ID generator to avoid external dependencies
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Store the current user email for syncing
let currentUserEmail: string | null = null;

export const setCurrentUserEmail = (email: string | null) => {
  currentUserEmail = email;
  // Sync from Supabase when user logs in - use forceSync to clear cache first
  if (email) {
    PreferencesService.forceSync().catch(err => {
      console.debug('Background sync failed:', err);
    });
  }
};

export const getCurrentUserEmail = (): string | null => {
  return currentUserEmail;
};

export interface GemDefinition {
  id: string;
  company: string;
  grade: string;
  isGem: boolean;
}

export interface ImportHistory {
  id: string;
  fileName: string;
  fileType: 'csv' | 'xlsx' | 'xls';
  importDate: string;
  recordCount: number;
  dataTypes: string[];
  status: 'success' | 'partial' | 'failed';
  errors?: string[];
}

export type SpreadsheetFormat = 'excel' | 'csv' | 'numbers' | 'google-sheets';

export interface UserPreferences {
  gemDefinitions: GemDefinition[];
  importHistory: ImportHistory[];
  dateFormat: string;
  dateSeparator: '/' | '-'; // Allow users to choose slash or dash separator
  currencyFormat: string;
  cardImagePreviewBehavior: 'hover' | 'click';
  cardImagePreviewSize: 'small' | 'medium' | 'large';
  defaultCalculationSettings: {
    roundingDecimals: number;
    includeFeesInTotalCost: boolean;
  };
  spreadsheetFormat: SpreadsheetFormat;
}

// Default gem definitions - 9.5 or 10 for all companies (except PSA which only has 10)
const DEFAULT_GEM_DEFINITIONS: GemDefinition[] = [
  // PSA (only 10 is gem, no 9.5 grade)
  { id: 'psa-10', company: 'PSA', grade: '10', isGem: true },
  
  // BGS
  { id: 'bgs-10', company: 'BGS', grade: '10', isGem: true },
  { id: 'bgs-9.5', company: 'BGS', grade: '9.5', isGem: true },
  
  // SGC
  { id: 'sgc-10', company: 'SGC', grade: '10', isGem: true },
  { id: 'sgc-9.5', company: 'SGC', grade: '9.5', isGem: true },
  
  // CSG
  { id: 'csg-10', company: 'CSG', grade: '10', isGem: true },
  { id: 'csg-9.5', company: 'CSG', grade: '9.5', isGem: true },
  
  // CGC
  { id: 'cgc-10', company: 'CGC', grade: '10', isGem: true },
  { id: 'cgc-9.5', company: 'CGC', grade: '9.5', isGem: true },
  
  
  // All other grades for these companies are not gems by default
  // Users can add custom rules if needed
];

const DEFAULT_PREFERENCES: UserPreferences = {
  gemDefinitions: DEFAULT_GEM_DEFINITIONS,
  importHistory: [],
  dateFormat: 'MM-DD-YYYY',
  dateSeparator: '-',
  currencyFormat: 'USD',
  cardImagePreviewBehavior: 'hover',
  cardImagePreviewSize: 'small',
  defaultCalculationSettings: {
    roundingDecimals: 2,
    includeFeesInTotalCost: true
  },
  spreadsheetFormat: 'excel'
};

export class PreferencesService {
  private static readonly STORAGE_KEY = 'sports-card-dashboard-preferences';
  private static syncInProgress = false;
  
  /**
   * Get user preferences from localStorage
   */
  static getPreferences(): UserPreferences {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all fields exist
        return {
          ...DEFAULT_PREFERENCES,
          ...parsed,
          gemDefinitions: parsed.gemDefinitions || DEFAULT_GEM_DEFINITIONS,
          defaultCalculationSettings: {
            ...DEFAULT_PREFERENCES.defaultCalculationSettings,
            ...parsed.defaultCalculationSettings
          }
        };
      }
    } catch (error) {
      console.warn('Failed to load preferences from localStorage:', error);
    }
    
    return DEFAULT_PREFERENCES;
  }
  
  /**
   * Save user preferences to localStorage and sync to Supabase
   */
  static savePreferences(preferences: UserPreferences): void {
    try {
      // Save to localStorage first (fast)
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(preferences));
      
      // Then sync to Supabase if user is logged in
      if (currentUserEmail) {
        const appSettings: AppSettings = {
          dateFormat: preferences.dateFormat,
          dateSeparator: preferences.dateSeparator,
          currencyFormat: preferences.currencyFormat,
          defaultCalculationSettings: preferences.defaultCalculationSettings,
          spreadsheetFormat: preferences.spreadsheetFormat,
          gemDefinitions: preferences.gemDefinitions,
          cardImagePreviewBehavior: preferences.cardImagePreviewBehavior,
          cardImagePreviewSize: preferences.cardImagePreviewSize,
        };
        
        // Sync to Supabase in background
        UserPreferencesService.saveAppSettings(currentUserEmail, appSettings).catch(err => {
          console.warn('Failed to sync preferences to Supabase:', err);
        });
      }
    } catch (error) {
      console.error('Failed to save preferences to localStorage:', error);
      throw new Error('Failed to save preferences');
    }
  }
  
  /**
   * Sync preferences from Supabase (called on login)
   */
  static async syncFromSupabase(): Promise<void> {
    // Get user email if not already set
    let email = currentUserEmail;
    if (!email) {
      try {
        const { supabase } = await import('./supabase');
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) {
          email = user.email;
          currentUserEmail = email;
        }
      } catch (error) {
        console.warn('Failed to get user from Supabase auth:', error);
      }
    }
    
    if (!email || this.syncInProgress) return;
    
    this.syncInProgress = true;
    try {
      console.log('[PreferencesService] syncFromSupabase - getting app settings for:', email);
      const cloudSettings = await UserPreferencesService.getAppSettings(email);
      console.log('[PreferencesService] cloudSettings received:', cloudSettings);
      
      if (cloudSettings) {
        // Get current local preferences
        const stored = localStorage.getItem(this.STORAGE_KEY);
        const localPrefs = stored ? JSON.parse(stored) : {};
        console.log('[PreferencesService] localPrefs:', localPrefs);
        
        // Merge cloud settings with local (cloud takes priority for synced fields)
        const mergedPrefs: UserPreferences = {
          ...DEFAULT_PREFERENCES,
          ...localPrefs,
          dateFormat: cloudSettings.dateFormat || localPrefs.dateFormat || DEFAULT_PREFERENCES.dateFormat,
          dateSeparator: cloudSettings.dateSeparator || localPrefs.dateSeparator || DEFAULT_PREFERENCES.dateSeparator,
          currencyFormat: cloudSettings.currencyFormat || localPrefs.currencyFormat || DEFAULT_PREFERENCES.currencyFormat,
          spreadsheetFormat: cloudSettings.spreadsheetFormat || localPrefs.spreadsheetFormat || DEFAULT_PREFERENCES.spreadsheetFormat,
          gemDefinitions: cloudSettings.gemDefinitions || localPrefs.gemDefinitions || DEFAULT_GEM_DEFINITIONS,
          cardImagePreviewBehavior: cloudSettings.cardImagePreviewBehavior || localPrefs.cardImagePreviewBehavior || DEFAULT_PREFERENCES.cardImagePreviewBehavior,
          cardImagePreviewSize: cloudSettings.cardImagePreviewSize || localPrefs.cardImagePreviewSize || DEFAULT_PREFERENCES.cardImagePreviewSize,
          defaultCalculationSettings: {
            ...DEFAULT_PREFERENCES.defaultCalculationSettings,
            ...localPrefs.defaultCalculationSettings,
            ...cloudSettings.defaultCalculationSettings,
          },
          // Keep import history local only
          importHistory: localPrefs.importHistory || [],
        };
        
        console.log('[PreferencesService] mergedPrefs:', mergedPrefs);
        
        // Save merged preferences locally
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(mergedPrefs));
        console.log('Preferences synced from Supabase');
      } else {
        console.log('[PreferencesService] No cloud settings found, keeping local preferences');
      }
    } catch (error) {
      console.warn('Failed to sync from Supabase:', error);
    } finally {
      this.syncInProgress = false;
    }
  }
  
  /**
   * Force a full sync from Supabase
   */
  static async forceSync(): Promise<UserPreferences> {
    console.log('[PreferencesService] forceSync called, currentUserEmail:', currentUserEmail);
    
    // Get user email directly from Supabase auth if not already set
    let email = currentUserEmail;
    if (!email) {
      try {
        console.log('[PreferencesService] No currentUserEmail, getting from Supabase auth...');
        const { supabase } = await import('./supabase');
        const { data: { user } } = await supabase.auth.getUser();
        console.log('[PreferencesService] User from auth:', user?.email);
        if (user?.email) {
          email = user.email;
          currentUserEmail = email; // Set it for future use
        }
      } catch (error) {
        console.warn('Failed to get user from Supabase auth:', error);
      }
    }
    
    if (email) {
      console.log('[PreferencesService] Starting sync for email:', email);
      await UserPreferencesService.syncFromDatabase(email);
      await this.syncFromSupabase();
    } else {
      console.log('[PreferencesService] No email available, skipping sync');
    }
    
    const finalPrefs = this.getPreferences();
    console.log('[PreferencesService] forceSync returning:', finalPrefs);
    return finalPrefs;
  }
  
  /**
   * Get gem definitions
   */
  static getGemDefinitions(): GemDefinition[] {
    return this.getPreferences().gemDefinitions;
  }
  
  /**
   * Update gem definitions
   */
  static updateGemDefinitions(gemDefinitions: GemDefinition[]): void {
    const preferences = this.getPreferences();
    preferences.gemDefinitions = gemDefinitions;
    this.savePreferences(preferences);
  }
  
  /**
   * Add a custom gem definition
   */
  static addGemDefinition(company: string, grade: string, isGem: boolean): void {
    const preferences = this.getPreferences();
    const id = `${company.toLowerCase()}-${grade.replace('.', '')}`;
    
    // Check if definition already exists
    const existingIndex = preferences.gemDefinitions.findIndex(
      def => def.company === company && def.grade === grade
    );
    
    if (existingIndex >= 0) {
      // Update existing
      preferences.gemDefinitions[existingIndex] = { id, company, grade, isGem };
    } else {
      // Add new
      preferences.gemDefinitions.push({ id, company, grade, isGem });
    }
    
    this.savePreferences(preferences);
  }
  
  /**
   * Remove a gem definition
   */
  static removeGemDefinition(company: string, grade: string): void {
    const preferences = this.getPreferences();
    preferences.gemDefinitions = preferences.gemDefinitions.filter(
      def => !(def.company === company && def.grade === grade)
    );
    this.savePreferences(preferences);
  }
  
  /**
   * Reset gem definitions to defaults
   */
  static resetGemDefinitions(): void {
    const preferences = this.getPreferences();
    preferences.gemDefinitions = [...DEFAULT_GEM_DEFINITIONS];
    this.savePreferences(preferences);
  }
  
  /**
   * Check if a specific grade qualifies as a gem
   */
  static isGemGrade(company: string, grade: string): boolean {
    const definitions = this.getGemDefinitions();
    const definition = definitions.find(
      def => def.company.toUpperCase() === company.toUpperCase() && 
             def.grade === grade
    );
    
    // If no specific definition found, use default logic (9.5 or 10, except PSA only has 10)
    if (!definition) {
      if (company.toUpperCase() === 'PSA') {
        return grade === '10';
      }
      return grade === '10' || grade === '9.5';
    }
    
    return definition.isGem;
  }
  
  /**
   * Get all companies that have gem definitions
   */
  static getCompanies(): string[] {
    const definitions = this.getGemDefinitions();
    const companies = new Set(definitions.map(def => def.company));
    return Array.from(companies).sort();
  }
  
  /**
   * Get all grades for a specific company
   */
  static getGradesForCompany(company: string): string[] {
    const definitions = this.getGemDefinitions();
    return definitions
      .filter(def => def.company === company)
      .map(def => def.grade)
      .sort();
  }
  
  /**
   * Update general preferences
   */
  static updateGeneralPreferences(updates: Partial<Omit<UserPreferences, 'gemDefinitions'>>): void {
    const preferences = this.getPreferences();
    Object.assign(preferences, updates);
    this.savePreferences(preferences);
  }
  
  /**
   * Reset all preferences to defaults
   */
  static resetAllPreferences(): void {
    this.savePreferences(DEFAULT_PREFERENCES);
  }
  
  /**
   * Add a new import to history
   */
  static addImportHistory(importData: Omit<ImportHistory, 'id'>): void {
    const preferences = this.getPreferences();
    const newImport: ImportHistory = {
      ...importData,
      id: generateId()
    };
    preferences.importHistory.unshift(newImport); // Add to beginning
    
    // Keep only last 50 imports
    if (preferences.importHistory.length > 50) {
      preferences.importHistory = preferences.importHistory.slice(0, 50);
    }
    
    this.savePreferences(preferences);
  }
  
  /**
   * Get import history
   */
  static getImportHistory(): ImportHistory[] {
    const preferences = this.getPreferences();
    return preferences.importHistory;
  }
  
  /**
   * Clear import history
   */
  static clearImportHistory(): void {
    const preferences = this.getPreferences();
    preferences.importHistory = [];
    this.savePreferences(preferences);
  }
}
